import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Calendar, FileText, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  paid: 'text-green-600', pending: 'text-amber-600', overdue: 'text-red-600',
  scheduled: 'text-teal-600', confirmed: 'text-teal-600', completed: 'text-gray-500',
};

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => { window.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick); };
  }, []);

  const doSearch = (q) => {
    clearTimeout(debounceRef.current);
    if (q.trim().length < 2) { setResults(null); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/search?q=${encodeURIComponent(q.trim())}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setResults(await res.json());
      } catch { /* ignore */ }
      setLoading(false);
    }, 300);
  };

  const handleChange = (e) => {
    setQuery(e.target.value);
    setOpen(true);
    doSearch(e.target.value);
  };

  const go = (path) => {
    setOpen(false);
    setQuery('');
    setResults(null);
    navigate(path);
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const hasResults = results && (results.customers.length || results.appointments.length || results.invoices.length);

  return (
    <div ref={wrapRef} className="relative w-72" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => query.trim().length >= 2 && setOpen(true)}
          placeholder="Suche… (Ctrl+K)"
          className="w-full h-9 pl-9 pr-8 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/40 transition-colors"
          data-testid="global-search-input"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" size={14} />}
      </div>

      {open && query.trim().length >= 2 && (
        <div
          className="absolute top-11 left-0 w-[26rem] max-h-[70vh] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl z-50 py-2"
          data-testid="global-search-results"
        >
          {!hasResults && !loading && (
            <div className="px-4 py-6 text-sm text-gray-500 text-center" data-testid="global-search-empty">
              Keine Treffer für „{query}“
            </div>
          )}

          {results?.customers?.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase flex items-center gap-1.5">
                <Users size={12} /> Kunden
              </div>
              {results.customers.map((c, i) => (
                <button
                  key={c.id}
                  onClick={() => go(`/customers?open=${c.user_id || c.id}`)}
                  className="w-full text-left px-4 py-2 hover:bg-teal-50 flex items-center justify-between gap-2"
                  data-testid={`search-result-customer-${i}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{c.first_name} {c.last_name}</div>
                    <div className="text-xs text-gray-500 truncate">{c.email}</div>
                  </div>
                  <span className="text-xs font-mono text-gray-400 shrink-0">{c.customer_number || ''}</span>
                </button>
              ))}
            </div>
          )}

          {results?.appointments?.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase flex items-center gap-1.5 border-t border-gray-100 mt-1 pt-2">
                <Calendar size={12} /> Termine
              </div>
              {results.appointments.map((a, i) => (
                <button
                  key={a.id}
                  onClick={() => go(`/appointments?date=${(a.start_time || '').slice(0, 10)}&selected=${a.id}`)}
                  className="w-full text-left px-4 py-2 hover:bg-teal-50"
                  data-testid={`search-result-appointment-${i}`}
                >
                  <div className="text-sm font-medium text-gray-800 truncate">
                    {(a.customer_names || []).join(', ') || a.customer_name || '—'} · {a.service_name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {fmtDate(a.start_time)} · {a.trainer_name || ''}
                  </div>
                </button>
              ))}
            </div>
          )}

          {results?.invoices?.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-xs font-semibold text-gray-400 uppercase flex items-center gap-1.5 border-t border-gray-100 mt-1 pt-2">
                <FileText size={12} /> Rechnungen
              </div>
              {results.invoices.map((inv, i) => (
                <button
                  key={inv.id}
                  onClick={() => go(`/invoices?search=${encodeURIComponent(inv.invoice_number || query.trim())}`)}
                  className="w-full text-left px-4 py-2 hover:bg-teal-50 flex items-center justify-between gap-2"
                  data-testid={`search-result-invoice-${i}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">{inv.invoice_number}</div>
                    <div className="text-xs text-gray-500 truncate">{inv.customer_name || ''}</div>
                  </div>
                  <span className={`text-xs shrink-0 ${statusColors[inv.status] || 'text-gray-500'}`}>
                    CHF {(inv.total_amount ?? inv.total ?? 0).toFixed ? (inv.total_amount ?? inv.total ?? 0).toFixed(2) : inv.total_amount}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
